import { memo } from 'react';
import { PropsWithChildren } from 'react';
import { ComponentMemo } from '@headless-workspace/typings';
import {
    GlowLink,
    isPropsWithFormSubmit,
    isPropsWithHref,
    PropsWithAriaControls,
    PropsWithAriaExpanded,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithCTA,
    PropsWithId,
    PropsWithStructuredData,
    PropsWithStyle,
    PropsWithSuppressHydrationWarning,
    PropsWithTestId,
} from '../../../../src';

type GlowCTAProps = PropsWithAriaLabel &
    PropsWithClassName &
    PropsWithId &
    PropsWithTestId &
    PropsWithStyle &
    PropsWithSuppressHydrationWarning &
    PropsWithCTA &
    PropsWithChildren &
    PropsWithAriaControls &
    PropsWithAriaExpanded &
    PropsWithStructuredData;

/**
 * Generic flat CTA that will render a button or anchor depending on the props
 * @param ariaLabel - Aria label for the button or anchor
 * @param className - Additional styles for the button or anchor
 * @param id - ID of the button or the anchor
 * @param style - Additional styles for the button or anchor
 * @param suppressHydrationWarning - If true, suppress the hydration warning
 * @param children - Content of the button or anchor
 * @param dataTestId - data-testid for the button or anchor
 * @param dataLayer - Data layer for the button or anchor
 * @param ariaControls - ID of the element controlled by the button
 * @param ariaExpanded - If the element controlled by the button is expanded
 * @param structuredData - Optional structured data for SEO. Can contain the following:
 * @param structuredData.itemScope - Indicates that defines the scope of associated metadata
 * @param structuredData.itemProp - Used to add properties to an item
 * @param structuredData.itemType - Specifies the URL of the vocabulary that will be used to define itemprop's (item properties) in the data structure
 * @param structuredData.content - The content of the structured data
 * @param props - Contains the following:
 * @param props.onMouseEnter - Function to be called when the mouse enters the button
 * @param props.onMouseLeave - Function to be called when the mouse leaves the button
 * @param props.onClick - Function to be called when the button is clicked
 * @param props.onKeyDown - Function to be called when a key is pressed
 * @param props.href - URL to navigate to when the anchor is clicked
 * @param props.isObfuscated - If true, the anchor will have an obscured URL
 */
export const GlowCTA: ComponentMemo<GlowCTAProps> = memo(
    ({
        ariaLabel,
        className,
        id,
        dataTestId,
        style,
        suppressHydrationWarning = false,
        children,
        dataLayer,
        ariaControls,
        ariaExpanded,
        structuredData,
        ...props
    }) => {
        if (isPropsWithHref(props)) {
            return (
                <GlowLink
                    id={id}
                    dataTestId={dataTestId}
                    className={className}
                    ariaLabel={ariaLabel}
                    style={style}
                    href={props.href}
                    titleReferent={props.titleReferent}
                    target={props.target}
                    isObfuscated={props.isObfuscated}
                    dataLayer={dataLayer}
                    disabled={props.disabled}
                    structuredData={structuredData}
                >
                    {children}
                </GlowLink>
            );
        }

        if (isPropsWithFormSubmit(props)) {
            return (
                <button
                    id={id}
                    data-testid={dataTestId}
                    className={className}
                    aria-label={ariaLabel}
                    style={style}
                    suppressHydrationWarning={suppressHydrationWarning}
                    type={'submit'}
                    form={props.formId}
                    data-layer-id={dataLayer?.id}
                    data-layer-sub-id={dataLayer?.subId}
                    data-layer-label={dataLayer?.label}
                    data-layer-action={dataLayer?.action}
                    itemProp={structuredData?.itemProp}
                    itemScope={structuredData?.itemScope}
                    itemType={structuredData?.itemType}
                    content={structuredData?.content}
                >
                    {children}
                </button>
            );
        }

        return (
            <button
                id={id}
                data-testid={dataTestId}
                className={className}
                aria-label={ariaLabel}
                style={style}
                suppressHydrationWarning={suppressHydrationWarning}
                onClick={props.onClick}
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
                onKeyDown={props.onKeyDown}
                type={'button'}
                disabled={props.disabled}
                data-layer-id={dataLayer?.id}
                data-layer-sub-id={dataLayer?.subId}
                data-layer-label={dataLayer?.label}
                data-layer-action={dataLayer?.action}
                aria-controls={ariaControls}
                aria-expanded={ariaExpanded}
                itemProp={structuredData?.itemProp}
                itemScope={structuredData?.itemScope}
                itemType={structuredData?.itemType}
                content={structuredData?.content}
            >
                {children}
            </button>
        );
    },
);

GlowCTA.displayName = 'GlowCTA';
